import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { MatchStatsAttempt } from '@/hooks/useMatchStatsFeedSSE';
import { getMatchStatsAttemptData } from '@/api/live-scoring/getMatchStatsAttemptData';

interface TeamStat {
  teamId: string;
  fixtureId: string;
  statType: string;
  value: number;
}

interface PlayerStat {
  playerId: string;
  playerName: string;
  teamId: string;
  fixtureId: string;
  statType: string;
  value: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attempt: MatchStatsAttempt | null;
  fixtureId: string;
}

export function MatchStatsFeedDetailDialog({ open, onOpenChange, attempt, fixtureId }: Props) {
  const [data, setData] = useState<{
    teamStats: TeamStat[];
    playerStats: PlayerStat[];
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !attempt) {
      setData(null);
      return;
    }

    setLoading(true);

    getMatchStatsAttemptData(fixtureId, attempt.id)
      .then(json => {
        setData(json);
      })
      .catch(() => {
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, attempt, fixtureId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Attempt Detail</DialogTitle>
          {attempt && (
            <p className="text-sm text-muted-foreground">
              {new Date(attempt.timestamp).toLocaleString()} &mdash; {attempt.durationMs}ms
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : !data ? (
          <p className="py-6 text-center text-muted-foreground">No data available</p>
        ) : (
          <Tabs defaultValue="team" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full">
              <TabsTrigger value="team" className="flex-1">
                Team Stats ({data.teamStats.length})
              </TabsTrigger>
              <TabsTrigger value="player" className="flex-1">
                Player Stats ({data.playerStats.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="team" className="flex-1 overflow-auto mt-2">
              {data.teamStats.length === 0 ? (
                <p className="py-6 text-center text-muted-foreground">No team stats available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team ID</TableHead>
                      <TableHead>Stat Type</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.teamStats.map((stat, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{stat.teamId}</TableCell>
                        <TableCell>{stat.statType}</TableCell>
                        <TableCell className="text-right">{stat.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="player" className="flex-1 overflow-auto mt-2">
              {data.playerStats.length === 0 ? (
                <p className="py-6 text-center text-muted-foreground">No player stats available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Player</TableHead>
                      <TableHead>Team ID</TableHead>
                      <TableHead>Stat Type</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.playerStats.map((stat, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="font-medium">{stat.playerName}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {stat.playerId}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{stat.teamId}</TableCell>
                        <TableCell>{stat.statType}</TableCell>
                        <TableCell className="text-right">{stat.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
